import type { SharedData } from "@src/context/ApplicationContext";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useCanvasContext } from "@src/context/CanvasContext";
import { useUTMContext } from "@src/context/UTMContext";
import { useEntryDigitalInfoBootstrap } from "@src/hooks/useEntryDigitalInfoBootstrap";
import { useEntryUserRoleBootstrap } from "@src/hooks/useEntryUserRoleBootstrap";
import { useUserLeadBootstrap } from "@src/hooks/useUserLeadBootstrap";
import { ensureUserUuid } from "@src/utils/entry-route-bootstrap";
import { useEffect, useRef } from "react";

interface UseEntryRouteBootstrapOptions {
  initReady?: boolean;
  isReferrerFlow?: boolean;
  enableUuid?: boolean;
  canEnsureUuid?: boolean;
  forceRegenerateUuid?: boolean;
  regenerateUuidOnEntry?: boolean;
  onUserUuidCreated?: (userUuid: string) => void;
  enableUserRole?: boolean;
  enableDigitalInfo?: boolean;
  digitalInfoLogLocation?: string;
}

interface UseEntryRouteBootstrapResult {
  resolvedUserRole?: SharedData["userRole"];
  isUserUuidReady: boolean;
}

export const useEntryRouteBootstrap = ({
  initReady = true,
  isReferrerFlow,
  enableUuid = true,
  canEnsureUuid,
  forceRegenerateUuid = false,
  regenerateUuidOnEntry = false,
  onUserUuidCreated,
  enableUserRole = true,
  enableDigitalInfo = true,
  digitalInfoLogLocation,
}: UseEntryRouteBootstrapOptions): UseEntryRouteBootstrapResult => {
  const { data, setData } = useApplicationContext();
  const { isInSalesforce: canvasIsInSalesforce, isLoading: canvasIsLoading } = useCanvasContext();
  const { utmTags } = useUTMContext();
  const userUuid = data.userUuid;
  const hasPostedDigitalInfo = data.hasPostedDigitalInfo;
  const canvasPrefillVersion = data.canvasPrefillVersion;
  const salesforceLeadId = data.salesforceLeadId;
  const userRecordCreated = data.userRecordCreated;
  const hasRegeneratedUuidOnEntryRef = useRef(false);
  const hasLeadScopedCanvasSession = Boolean(data.salesforceLeadId?.trim());
  const currentUserRole = data.userRole;
  const resolvedIsReferrerFlow = isReferrerFlow ?? data.isReferrerFlow;

  const isBootstrapEnabled = initReady;
  const canEnsureUuidInCurrentFlow =
    !canvasIsInSalesforce || hasLeadScopedCanvasSession || forceRegenerateUuid;
  const canEnsureUuidForRoute = canEnsureUuid ?? true;
  const shouldRegenerateUuidOnEntry =
    regenerateUuidOnEntry && !hasRegeneratedUuidOnEntryRef.current;
  const shouldWaitForRouteEntryUuid = regenerateUuidOnEntry && shouldRegenerateUuidOnEntry;

  useEffect(() => {
    if (
      !isBootstrapEnabled ||
      !enableUuid ||
      !canEnsureUuidInCurrentFlow ||
      !canEnsureUuidForRoute
    ) {
      return;
    }

    if (regenerateUuidOnEntry && hasRegeneratedUuidOnEntryRef.current) {
      return;
    }

    const result = ensureUserUuid({
      currentUserUuid: userUuid,
      setData,
      forceRegenerate: forceRegenerateUuid || shouldRegenerateUuidOnEntry,
    });

    if (result.created) {
      if (shouldRegenerateUuidOnEntry) {
        hasRegeneratedUuidOnEntryRef.current = true;
        setData("hasPostedDigitalInfo", false);
      }
      onUserUuidCreated?.(result.userUuid);
    }
  }, [
    isBootstrapEnabled,
    enableUuid,
    canEnsureUuidInCurrentFlow,
    canEnsureUuidForRoute,
    userUuid,
    setData,
    forceRegenerateUuid,
    regenerateUuidOnEntry,
    shouldRegenerateUuidOnEntry,
    onUserUuidCreated,
  ]);

  const resolvedUserRole = useEntryUserRoleBootstrap({
    enabled: isBootstrapEnabled && enableUserRole,
    canvasIsInSalesforce,
    canvasIsLoading,
    isReferrerFlow: resolvedIsReferrerFlow,
    currentUserRole,
    setData,
  });

  useEntryDigitalInfoBootstrap({
    enabled: isBootstrapEnabled && enableDigitalInfo && !shouldWaitForRouteEntryUuid,
    userUuid,
    hasPostedDigitalInfo,
    forcePostForUserUuid: regenerateUuidOnEntry,
    canvasIsInSalesforce,
    canvasIsLoading,
    canvasPrefillVersion,
    setData,
    utmTags,
    logLocation: digitalInfoLogLocation || "EntryRouteBootstrap",
  });

  useUserLeadBootstrap({
    enabled: isBootstrapEnabled,
    userUuid,
    salesforceLeadId,
    canvasPrefillVersion,
    userRole: resolvedUserRole,
    userRecordCreated,
  });

  return {
    resolvedUserRole,
    isUserUuidReady: !shouldWaitForRouteEntryUuid,
  };
};
